import { useState } from "react";
import { Button } from '../../core/index.js'
import { Send } from 'lucide-react'

const REPLY_MAX_LENGTH = 1000;

interface BarrierReplyFormProps {
  /** Sends the words; the caller owns the mutation. */
  onSend: (message: string) => void;
  sending: boolean;
  error: string | null;
  onCancel: () => void;
}

/**
 * Where an operator or facility administrator answers a worker's note.
 * The reply goes to the worker in their own app and is kept as written,
 * once, so the form says so before anything is sent.
 */
export function BarrierReplyForm({
  onSend,
  sending,
  error,
  onCancel,
}: BarrierReplyFormProps) {
  const [message, setMessage] = useState("");
  const trimmed = message.trim();
  const canSend =
    trimmed.length > 0 && message.length <= REPLY_MAX_LENGTH && !sending;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (canSend) {
      onSend(trimmed);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-1.5 border-t border-marine/10 pt-2"
    >
      <label className="text-[10px] font-bold text-ink">
        Reply to this worker
        <textarea
          value={message}
          maxLength={REPLY_MAX_LENGTH}
          rows={3}
          autoFocus
          onChange={(event) => setMessage(event.target.value)}
          placeholder="What the facility will do, or what you would like to talk through. The worker reads this under their note."
          className="mt-1 w-full border border-marine/30 bg-white px-2 py-1.5 text-[12px] font-normal text-ink leading-snug"
        />
      </label>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] text-ink-mid">
          {message.length}/{REPLY_MAX_LENGTH} · one reply per note, kept as
          written
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="text-[10px] font-bold text-ink-mid hover:text-ink disabled:opacity-50"
          >
            Cancel
          </button>
          <Button icon={Send} size="sm" type="submit" disabled={!canSend}>
            {sending ? "Sending…" : "Send reply"}
          </Button>
        </div>
      </div>
      {error && <div className="text-[11px] text-coral">{error}</div>}
    </form>
  );
}
